/* regex_editor.control_adapter.js */

"use strict";

const $ = require("jquery");
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const q = require("q");
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const RegexControl = require("@gluenetworks/regex_control").RegexControlComponent;
const RegexMode = require("@gluenetworks/regex_control").RegexMode;
const StyleUtil = require("../StyleUtil");

class RegexEditorAdapter extends EventEmitter {
    static get isAngularControl() {
        return true;
    }

    static get isMultiEditSupported() {
        return false;
    }

    constructor(logger, containingElem, formCtrlDef) {
        super();

        this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
        this._required = false;
        this.containingElem = containingElem;
        this.formCtrlDef = formCtrlDef;
        this.logger = logger;

        this.name = this.formCtrlDef.name;
        this.oldVal = "";
        this.oldInvalidStatus = false;
        this.oldInProgressStatus = false;

        this._accordionDisplay = _.get(this.formCtrlDef, "configSettings.Display.accordion", false);

        this.$init.$inject = ["$scope", "$templateCache", "$timeout"];
        this.$compiledAndLinked.$inject = ["$scope", "$timeout"];
    }

    /**
     * @returns {string|Array} the data associated with this control.
     */
    getTestString() {
        let val = this.testStringEditor.getValue();
        if (val) {
            val = val.split(/\r?\n/);
        }
        return val;
    }

    /**
     * Set this controls current value.
     * @param val {string|Array} The value to set this control to.
     * @returns {string|Array} the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    setTestString(val) {
        let result = val;
        if (Array.isArray(val)) {
            result = val
                .map((element) => {
                    return _.isObject(element) ? element.value : element;
                })
                .join("\n");
        }
        this.testStringEditor.setValue(result);
    }

    getValue() {
        return {regex: this.regexInput.getValue(), testString: this.getTestString()};
    }

    setValue(val) {
        let regexVal = null,
            testString = null;
        if (!_.isEmpty(val) && _.isObject(val)) {
            regexVal = _.get(val, "regex", null);
            testString = _.get(val, "testString", null);
        }
        this.regexInput.setValue(regexVal);
        this.setTestStringEditorRegex(regexVal);
        this.setTestString(val.testString);
    }

    setTestStringEditorRegex(regex) {
        this.testStringEditor.setRegex(regex);
    }

    setReadOnly(flag) {
        this._readOnlyField = flag;
        this.regexInput.setDisabled(flag);
        this.testStringEditor.setDisabled(flag);
    }

    setRegexInputReadOnly(flag) {
        this.regexInput.setDisabled(flag);
    }

    setTestStringEditorReadOnly(flag) {
        this.testStringEditor.setDisabled(flag);
    }

    setHidden(hidden) {
        this.setRegexInputHidden(hidden);
        this.setTestStringEditorHidden(hidden);
    }

    setRegexInputHidden(hidden) {
        let visible = hidden && hidden === true ? false : true;
        this.regexInput.setVisible(visible);
    }

    setTestStringEditorHidden(hidden) {
        let visible = hidden && hidden === true ? false : true;
        this.testStringEditor.setVisible(visible);
    }

    setModeSelectorVisible(visible) {
        this.testStringEditor.setModeSelectorVisible(visible);
    }

    resizeTestStringEditor(event, data) {
        this.testStringEditor.resizeEditor(data.height);
    }

    isInProgress() {
        return this.testStringEditor.isInProgress();
    }

    isInvalid() {
        return (
            this.regexInput.isInvalid() ||
            this._getRegexValidationMessages(this.regexInput.value) !== null
        );
    }

    /**
     * Returns current validation violations on regex input text control.
     * @returns {Array}
     */
    getValidationMessages() {
        let violations = [];
        if (this.regexInput.isInvalid()) {
            violations = this.regexInput.getValidationMessages();
        } else {
            let regexValidViolation = this._getRegexValidationMessages(this.regexInput.value);
            if (regexValidViolation) {
                violations.push(regexValidViolation);
            }
        }
        return _.isEmpty(violations) ? {} : {regex: violations};
    }

    toggleTestStringDisplay() {
        let testStringHidden = !this.testStringEditor.visible;
        if (testStringHidden) {
            this._accordionIcon.classList.add("expanded");
        } else {
            this._accordionIcon.classList.remove("expanded");
        }
        this.setTestStringEditorHidden(!testStringHidden);
    }

    tearDown() {
        this.regexInput.removeListener("UserValue", this._onRegexValueChangeDebounce);
        this.regexInput.removeListener("InvalidUserInput", this._onInvalidStatusChange);
        this._accordionIcon.removeEventListener("click", this.toggleTestStringDisplay);
        this.testStringEditor.removeListener("UserValue", this._onValueChange);
        this.testStringEditor.removeListener("InProgress", this._onInProgressStatusChange);
        if (this.resizeTestStringEditorUnregister) {
            this.resizeTestStringEditorUnregister();
        }
        this.regexInput.shutdown();
        this.testStringEditor.shutdown();
    }

    /********************************************************************************/
    /* A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called. */
    /* @param $scope {$scope} Angular $scope                                        */
    /* @param $templateCache {$templateCache}                                       */
    /********************************************************************************/
    $init($scope, $templateCache /*, $timeout */) {
        let template = $templateCache.get("templatecache/templates/regex_editor.template.html");
        let container = $(template);
        this.regexInput = new InputTextControl({placeholder: "Enter regular expression here!"});
        this.regexInputValidationsDiv = document.createElement("div");
        this.regexInputValidationsDiv.classList.add("regex-input-validations");
        let regexEditorConfig = {
            minLineCount: _.get(
                this.formCtrlDef,
                "configSettings.Display.testString.minLineCount",
                5
            ),
            maxLineCount: _.get(
                this.formCtrlDef,
                "configSettings.Display.testString.maxLineCount",
                20
            ),
        };
        this.testStringEditor = new RegexControl(regexEditorConfig);
        let psuedoUUID = function () {
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
                let r = (Math.random() * 16) | 0,
                    v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        };
        this._accordionIcon = document.createElement("actionable-icon-gl");
        this._accordionIcon.classList.add("accordion-icon");
        this._accordionIcon.dataset.dark = true;
        this._accordionIcon.dataset.icon = "form.expand_collapse";

        this.containerId = psuedoUUID();
        container.attr("id", this.containerId);
        this.regexEditorContainer = container.find(".regex_editor_container");
        this.containingElem.append(container);
        let regexInputContainer = this.regexEditorContainer.find(".regex_text_container")[0];
        if (this._accordionDisplay) {
            regexInputContainer.append(this._accordionIcon);
            if (_.get(this._accordionDisplay, "default", "expanded") === "collapsed") {
                this.testStringEditor.setVisible(false);
            }
        }
        regexInputContainer.append(this.regexInput.renderer.rootElement);
        let testStringContainer = this.regexEditorContainer.find(".testString_container")[0];
        testStringContainer.before(this.regexInputValidationsDiv);
        testStringContainer.append(this.testStringEditor.renderer.rootElement);

        let stylesConfig = _.get(this.formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, this.containingElem[0]);

        // Apply item layout styles - these styles customize how this boolean element is placed in parent layout
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        this.containingElem.addClass(itemLayoutCssClass);

        this._scope = $scope;
        $scope.formCtrDef = this.formCtrlDef;

        this.emit("InitializationComplete", {
            initializedValue: this.getValue(),
        });

        this.loadDeferred = q.defer();
        return this.loadDeferred.promise;
    }

    /********************************************************************************/
    /* A Hook for when this control has been compiled/Linked.                       */
    /* @param $scope {$scope} The Angular Scope variable                            */
    /* @param $timeout The angular timeout object                                   */

    /* if $compiledAndLinked.$inject is specified those injection parameters will be */
    /* used as the arguments */
    /********************************************************************************/
    $compiledAndLinked($scope, $timeout) {
        this._required = _.get(this.formCtrlDef, "validationInfo.required", false);
        if (this._required) {
            this._required = true;
            this.regexInput.setRequired(true);
            this.regexInput.setValidationVerbiage({required: "Please enter a regular expression"});
        }

        if (_.has(this.formCtrlDef, "configSettings.Display.readOnly")) {
            this.setReadOnly(_.get(this.formCtrlDef, "configSettings.Display.readOnly"));
        } else {
            this.setRegexInputReadOnly(
                _.get(this.formCtrlDef, "configSettings.Display.regex.readOnly"),
                false
            );
            this.setTestStringEditorReadOnly(
                _.get(this.formCtrlDef, "configSettings.Display.testString.readOnly"),
                false
            );
        }

        if (_.has(this.formCtrlDef, "configSettings.Display.hidden")) {
            this.setHidden(_.get(this.formCtrlDef, "configSettings.Display.hidden", false));
        } else {
            this.setRegexInputHidden(
                _.get(this.formCtrlDef, "configSettings.Display.regex.hidden", false)
            );
            // Determine if the testString should be shown on two criteria
            // - First, is the text area hidden field set
            // - Second, if the hidden field is not set determine if the accordion should be expanded or collapsed.
            this.setTestStringEditorHidden(
                _.get(
                    this.formCtrlDef,
                    "configSettings.Display.testString.hidden",
                    _.get(
                        this.formCtrlDef,
                        "configSettings.Display.accordion.default",
                        "expanded"
                    ) === "collapsed"
                )
            );
            if (this.testStringEditor.visible) {
                this._accordionIcon.classList.add("expanded");
            }
        }
        this.testStringEditor.setLabel(
            _.get(this.formCtrlDef, "configSettings.Display.testString.label", null)
        );

        this.setModeSelectorVisible(
            _.get(this.formCtrlDef, "configSettings.Display.testString.modeSelectorVisible", true)
        );
        this.testStringEditor.setSectionDelimiter(
            _.get(this.formCtrlDef, "configSettings.Display.testString.sectionDelimiter", "")
        );
        let mode = _.get(this.formCtrlDef, "configSettings.Display.testString.mode", "normal");
        mode = RegexMode.isValidMode(mode) ? mode : RegexMode.MATCH_SINGLE_LINES.value;
        this.testStringEditor.setMode(mode);
        let value = this._buildValueObject(_.get(this.formCtrlDef, "configSettings.Regex", ""));
        if (!_.isNil(this.formCtrlDef.value)) {
            let configDataValue = this.formCtrlDef.value;
            if (_.isString(configDataValue)) {
                value.regex = configDataValue;
            } else if (_.isObject(configDataValue)) {
                if (configDataValue.hasOwnProperty("regex")) {
                    value.regex = configDataValue.regex;
                }
                if (configDataValue.hasOwnProperty("testString")) {
                    value.testString = configDataValue.testString;
                }
            }
        }
        this.setValue(value);
        this._onRegexValueChange(value.regex, "");
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }
        this.regexInput.on(
            "UserValue",
            (this._onRegexValueChangeDebounce = _.debounce(
                this._onRegexValueChange.bind(this),
                750
            ))
        );
        this.regexInput.on(
            "InvalidUserInput",
            (this._onInvalidStatusChange = this._onInvalidStatusChange.bind(this))
        );
        this._accordionIcon.addEventListener(
            "click",
            (this.toggleTestStringDisplay = this.toggleTestStringDisplay.bind(this))
        );
        this.testStringEditor.on(
            "UserValue",
            (this._onValueChange = this._onValueChange.bind(this))
        );
        this.testStringEditor.on(
            "InProgress",
            (this._onInProgressStatusChange = this._onInProgressStatusChange.bind(this))
        );
        this.resizeTestStringEditorUnregister = $scope.$on(
            "regex.editor.resize",
            this.resizeTestStringEditor.bind(this)
        );
        this.loadDeferred.resolve();
    }

    /********************************************************************************/
    /* This control can take a string or object (Eg: {regex: "", testString: []})   */
    /*                                                                              */
    /* @param inputValue (string|object)                                            */
    /* @returns object of the form {regex: "", testString: []}                      */
    /********************************************************************************/
    _buildValueObject(inputValue) {
        let returnValue = {};
        if (_.isString(inputValue)) {
            returnValue.regex = inputValue;
        } else if (!_.isEmpty(inputValue)) {
            if (inputValue.hasOwnProperty("regex")) {
                returnValue.regex = inputValue.regex;
            }
            if (inputValue.hasOwnProperty("testString")) {
                returnValue.testString = inputValue.testString;
            }
        }
        return returnValue;
    }

    _isValidLineCount(lineCount) {
        return lineCount && _.isInteger(lineCount) && lineCount > 0;
    }

    _onRegexValueChange(newVal, oldVal) {
        let regexValidationMessage = this._getRegexValidationMessages(newVal);
        let isRegexInvalid = !_.isNil(regexValidationMessage);
        if (!isRegexInvalid) {
            this.regexEditorContainer[0].classList.remove("invalid");
            this._setValidationMessagesOnRegexInput([]);
            this.setTestStringEditorRegex(newVal);
        } else {
            this.regexEditorContainer[0].classList.add("invalid");
            this.setTestStringEditorRegex("");
            this._setValidationMessagesOnRegexInput([regexValidationMessage]);
        }
        this._onValueChange(newVal, oldVal);
        this._onInvalidStatusChange(isRegexInvalid, this.oldInvalidStatus);
    }

    _onInvalidStatusChange() {
        let isInvalid = this.isInvalid();
        if (isInvalid !== this.oldInvalidStatus) {
            this.emit("Invalid", {
                name: this.formCtrlDef.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.formCtrlDef,
                oldValue: this.oldInvalidStatus,
                newValue: isInvalid,
            });
            this.oldInvalidStatus = isInvalid;
        }
    }

    _onValueChange(newVal, oldVal) {
        if (newVal !== oldVal) {
            this.emit("ValueChanged", {
                name: this.formCtrlDef.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.formCtrlDef,
                oldValue: this.oldVal,
                newValue: this.getValue(),
            });
            this.oldVal = this.getValue();
        }
    }

    _onInProgressStatusChange(newInProgressStatus, oldInProgressStatus) {
        if (newInProgressStatus !== oldInProgressStatus) {
            this.emit("InProgress", {
                name: this.formCtrlDef.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.formCtrlDef,
                oldInProgressStatus: this.oldInProgressStatus,
                newInProgressStatus: this.isInProgress(),
            });
            this.oldInProgressStatus = this.isInProgress();
        }
    }

    _setValidationMessagesOnRegexInput(validationMessages) {
        while (this.regexInputValidationsDiv.firstChild !== null) {
            this.regexInputValidationsDiv.firstChild.remove();
        }
        if (!_.isEmpty(validationMessages)) {
            validationMessages.forEach(function (validationMessage) {
                let validationMessageDiv = document.createElement("div");
                validationMessageDiv.textContent = validationMessage;
                this.regexInputValidationsDiv.appendChild(validationMessageDiv);
            }, this);
        }
    }

    /**
     * validate given regular expression string
     * @param regex
     * @returns {String} - validation error message
     * @private
     */
    _getRegexValidationMessages(regex) {
        let validationMessage = null;
        try {
            new RegExp(regex);
        } catch (e) {
            validationMessage = e.message;
        }
        return validationMessage;
    }

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
}

module.exports = RegexEditorAdapter;
